import { isAdminActor, type AuthActor } from '@command-center/auth-sdk';
import { parseStructuredOutputs, type StructuredOutputRecord } from './structured-outputs.js';
import {
  deriveVoiceAgentSetupStatus,
  redactSetupStatusForClient,
  type VoiceAgentSetupInputs,
  type VoiceAgentSetupStatus,
  type VoiceAgentSetupStatusPublic,
  type VoiceAgentSetupFailure,
} from './setup-status.js';
import {
  CALL_CATEGORIES,
  LEAD_GRADES,
  callCategoryStorageValues,
  knownCallCategoryStorageValues,
  normalizeCallCategory,
  type CallCategory,
} from './call-analysis.js';

export const VOICE_DEFAULT_PAGE_SIZE = 50;
export const VOICE_MAX_PAGE_SIZE = 100;
export const VOICE_CASCADE_NOT_CONFIGURED_MESSAGE =
  'Only Voice Agents linked to Cascade appear here. In One Up Admin, open Voice Agents, select Cascade as the management client, then import the Vapi assistant.';

export interface VoiceDataStore {
  select<T = Record<string, unknown>>(
    table: string,
    query?: Record<string, string | undefined>,
  ): Promise<T[]>;
}

export interface VoiceServiceOptions {
  /** Hard tenant boundary used by tenant-specific Workers such as Cascade. */
  forcedClientId?: string;
  /** Override admin visibility when a tenant portal uses a synthesized admin actor. */
  includeAdminFields?: boolean;
  recordingPathBase?: string;
  notConfiguredMessage?: string;
  /** Portal-owned Incoming Call Triage setting used for the Low Value badge. */
  lowValuePurposes?: string[];
  triageLabels?: {
    purposes?: Record<string, string>;
    callerRelationships?: Record<string, string>;
    leadEligibility?: Record<string, string>;
  };
}

export interface VoiceCallFilters {
  cursor?: string;
  limit?: number;
  clientIds?: string[];
  /** Tenants to subtract from the scope. Can only narrow — see VoiceTenantScope. */
  excludeClientIds?: string[];
  agentId?: string;
  direction?: string;
  status?: string;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
  hasIssue?: boolean;
  category?: string[];
  excludeCategory?: string[];
  leadGrade?: string[];
  sourceRating?: number[];
  excludeSourceRating?: number[];
  /** Portal feed partition. Main is actionable; Low Value is its complement. */
  feed?: 'main' | 'low_value';
  /** Portal-owned purposes that belong in Low Value. */
  lowValueCategories?: string[];
  followUp?: boolean;
  archived?: boolean;
  /** Admin-only. Rejected for non cross-tenant actors. */
  processingStatus?: string[];
}

export interface VoiceAgentFilters {
  clientIds?: string[];
  /** Tenants to subtract from the scope. Can only narrow — see VoiceTenantScope. */
  excludeClientIds?: string[];
  status?: string;
  search?: string;
  limit?: number;
}

export interface VoiceUsageFilters {
  clientIds?: string[];
  /** Tenants to subtract from the scope. Can only narrow — see VoiceTenantScope. */
  excludeClientIds?: string[];
  agentId?: string;
  direction?: string;
  status?: string;
  dateFrom?: string;
  dateTo?: string;
}

export interface VoiceCallerSummary {
  name?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
  businessName?: string;
}

export interface VoiceCallAdminFields {
  provider: 'vapi';
  providerCallId?: string;
  providerAssistantId?: string;
  providerPhoneNumberId?: string;
  totalCost?: number;
  costBreakdown?: unknown;
  airtableCallRecordId?: string;
  airtableAgentRecordId?: string;
  rawPayloadId?: string;
  linkedTaskId?: string;
  linkedLeadId?: string;
  linkedContactId?: string;
  linkedOrganizationId?: string;
  leadQuality?: string;
  nextAction?: string;
  debugMetadata?: unknown;
}

export interface VoiceCallSummary {
  id: string;
  clientId: string;
  agentId?: string;
  agentName?: string;
  caller: VoiceCallerSummary;
  direction?: string;
  status?: string;
  outcome?: string;
  startedAt?: string;
  endedAt?: string;
  durationSeconds?: number;
  durationMinutes?: number;
  summary?: string;
  callCategory?: string;
  callCategoryLabel?: string;
  callerRelationship?: string;
  callerRelationshipLabel?: string;
  leadEligibility?: string;
  leadEligibilityLabel?: string;
  triageReasoning?: string;
  isLowValue?: boolean;
  archived?: boolean;
  archivedAt?: string;
  leadGrade?: string;
  /** Original 1-5 rating supplied by the source system, when present. */
  sourceRating?: number;
  /** Provider sentiment label, when an analysis plan reports one. */
  sentiment?: string;
  /** Raw lead-promise score on the provider's own scale (typically 1-10). */
  leadScore?: number;
  /** Raw assistant-performance score on the provider's own scale. */
  assistantScore?: number;
  followUpRequired: boolean;
  transcriptAvailable: boolean;
  recordingAvailable: boolean;
  hasIssue: boolean;
  admin?: VoiceCallAdminFields;
}

export interface VoiceCallActionSummary {
  type: string;
  status: string;
  targetType?: string;
  createdAt: string;
}

export interface VoiceToolResultSummary {
  name?: string;
  status: string;
  createdAt: string;
}

export interface VoiceOperationalEvent {
  id: string;
  receivedAt: string;
  eventType?: string;
  status: string;
  error?: string;
  payload?: unknown;
}

export interface VoiceOperationalToolCall extends VoiceToolResultSummary {
  id: string;
  providerToolCallId?: string;
  parameters?: unknown;
  result?: unknown;
  error?: string;
}

export interface VoiceOperationalAction extends VoiceCallActionSummary {
  id: string;
  targetId?: string;
  payload?: unknown;
  result?: unknown;
  createdBy?: string;
}

export interface VoiceCallDetail extends VoiceCallSummary {
  transcript?: string;
  structuredIntake?: unknown;
  /**
   * Per-output analysis results, shaped for buildCallPresentation. Client-safe:
   * these are the assistant's own conclusions about the call, not provider
   * internals.
   */
  structuredOutputs?: StructuredOutputRecord[];
  gradeExplanation?: string;
  recommendedNextAction?: string;
  /**
   * Canonical Supabase contact. Tenant-scoped, so it is client-safe — unlike
   * provider identifiers it never reveals another tenant or the provider account.
   */
  contactId?: string;
  notes?: VoiceRecordNote[];
  triageHistory?: VoiceCallTriageChange[];
  actions: VoiceCallActionSummary[];
  toolResults: VoiceToolResultSummary[];
  recordingUrl?: string;
  admin?: VoiceCallAdminFields & {
    events: VoiceOperationalEvent[];
    toolCalls: VoiceOperationalToolCall[];
    actionHistory: VoiceOperationalAction[];
    ingestionStatus?: string;
    ingestionError?: string;
  };
}

export interface VoiceRecordNote {
  id: string;
  body: string;
  authorId?: string;
  authorName: string;
  createdAt: string;
}

export interface VoiceCallTriageChange {
  id: string;
  previousCallerRelationship?: string;
  newCallerRelationship: string;
  previousCallPurpose?: string;
  newCallPurpose: string;
  previousLeadEligibility?: string;
  newLeadEligibility: string;
  changedById?: string;
  changedByName: string;
  changedAt: string;
}

export interface VoiceCallsResponse {
  items: VoiceCallSummary[];
  nextCursor: string | null;
  limit: number;
}

export interface VoicePromptDeploymentSummary {
  id: string;
  compiledPrompt?: string;
  promptHash?: string;
  generatedBy?: string;
  reviewedBy?: string;
  reviewedAt?: string;
  deployed: boolean;
  deployedAt?: string;
  result?: unknown;
  error?: string;
  businessContext?: unknown;
  createdAt: string;
}

export interface VoiceOutboundRequestSummary {
  id: string;
  providerCallId?: string;
  targetPhone: string;
  targetName?: string;
  purpose?: string;
  status: string;
  error?: string;
  requestedBy?: string;
  requestedAt: string;
  startedAt?: string;
  completedAt?: string;
}

export interface VoiceAgentAdminFields {
  provider: 'vapi';
  providerAssistantId?: string;
  providerPhoneNumberId?: string;
  airtableAgentRecordId?: string;
  currentPromptHash?: string;
  currentSettings?: unknown;
  compiledPrompt?: string;
  currentPromptDeployment?: VoicePromptDeploymentSummary;
  /** Full canonical setup status, including missing requirements and next action. */
  setup?: VoiceAgentSetupStatus;
}

export interface VoiceAgentSummary {
  id: string;
  /** Portal-owned Agent record linked to this runtime variant, when managed. */
  voiceAgentId?: string;
  clientId: string;
  displayName: string;
  /** Organization-managed portal avatar. Never contains provider credentials. */
  avatarUrl?: string;
  slug: string;
  purpose?: string;
  status: string;
  isDefaultInbound: boolean;
  mapped: boolean;
  phoneMapped: boolean;
  createdAt: string;
  updatedAt: string;
  /**
   * Canonical, server-derived setup status (client-safe subset). Every audience
   * renders the same machine state and friendly label; admins additionally
   * receive the full status under `admin.setup`.
   */
  setupStatus: VoiceAgentSetupStatusPublic;
  metrics: {
    recentCallCount: number;
    lastCallAt?: string;
    followUpCount: number;
    issueCount: number;
    statusIndicator: 'ok' | 'warning' | 'error' | 'unknown';
  };
  admin?: VoiceAgentAdminFields;
}

export interface VoiceAgentDetail extends VoiceAgentSummary {
  recentCalls: VoiceCallSummary[];
  admin?: VoiceAgentAdminFields & {
    recentEvents: VoiceOperationalEvent[];
    recentToolCalls: VoiceOperationalToolCall[];
    recentOutboundRequests: VoiceOutboundRequestSummary[];
  };
}

export interface VoiceConfigurationState {
  status: 'not_configured';
  message: string;
  requiredProductionData: [
    'Vapi assistant ID',
    'Vapi phone number ID',
    'Variant slug',
    'client_id',
    'Agent/persona relationship',
    'Active/mapped status',
  ];
}

export interface VoiceAgentsResponse {
  items: VoiceAgentSummary[];
  total: number;
  configuration?: VoiceConfigurationState;
}

export interface VoiceUsageBucket {
  clientId: string;
  callCount: number;
  totalDurationSeconds: number;
  totalMinutes: number;
  inboundCallCount: number;
  outboundCallCount: number;
  followUpRequiredCount: number;
  issueCount: number;
  totalCost?: number;
}

export interface VoiceUsageSummary {
  clientIds: string[] | null;
  dateFrom: string | null;
  dateTo: string | null;
  callCount: number;
  totalDurationSeconds: number;
  totalMinutes: number;
  averageDurationSeconds: number;
  inboundCallCount: number;
  outboundCallCount: number;
  followUpRequiredCount: number;
  issueCount: number;
  totalCost?: number;
  byClient: VoiceUsageBucket[];
  truncated: boolean;
}

export interface VoiceRecordingReference {
  callId: string;
  providerCallId?: string;
  recordingUrl?: string;
  stereoRecordingUrl?: string;
  available: boolean;
}

export class VoiceServiceError extends Error {
  constructor(
    public readonly code: 'validation_error' | 'not_found' | 'forbidden',
    message: string,
    public readonly status: 400 | 403 | 404,
  ) {
    super(message);
    this.name = 'VoiceServiceError';
  }
}

interface VapiCallRow {
  id: string;
  vapi_call_id: string | null;
  airtable_call_record_id: string | null;
  airtable_agent_record_id: string | null;
  agent_variant_id: string | null;
  client_id: string | null;
  direction: string | null;
  phone_number: string | null;
  customer_number: string | null;
  caller_name: string | null;
  started_at: string | null;
  ended_at: string | null;
  duration_seconds: number | string | null;
  duration_minutes?: number | string | null;
  total_cost?: number | string | null;
  cost_breakdown_json?: unknown;
  status: string | null;
  ended_reason: string | null;
  summary: string | null;
  transcript?: string | null;
  transcript_available?: boolean | null;
  recording_url: string | null;
  stereo_recording_url: string | null;
  analysis_json?: unknown;
  structured_data_json?: unknown;
  call_disposition: string | null;
  call_category: string | null;
  caller_relationship?: string | null;
  lead_eligibility?: string | null;
  triage_reasoning?: string | null;
  lead_quality: string | null;
  source_rating?: number | string | null;
  sentiment?: string | null;
  lead_score?: number | string | null;
  assistant_score?: number | string | null;
  structured_outputs_json?: unknown;
  grade_explanation: string | null;
  follow_up_required: boolean | null;
  next_action: string | null;
  contact_id: string | null;
  raw_payload_id: string | null;
  linked_task_id?: string | null;
  linked_lead_id?: string | null;
  linked_contact_id?: string | null;
  linked_organization_id?: string | null;
  action_metadata_json?: unknown;
  has_issue?: boolean | null;
  created_at: string;
  updated_at: string;
  archived_at?: string | null;
  archived_by?: string | null;
}

interface RecordNoteRow {
  id: string;
  body: string;
  author_id: string | null;
  author_name: string;
  created_at: string;
}

interface CallTriageChangeRow {
  id: string;
  previous_caller_relationship: string | null;
  new_caller_relationship: string;
  previous_call_purpose: string | null;
  new_call_purpose: string;
  previous_lead_eligibility: string | null;
  new_lead_eligibility: string;
  changed_by_id: string | null;
  changed_by_name: string;
  changed_at: string;
}

interface VoiceAgentVariantRow {
  id: string;
  airtable_agent_record_id: string | null;
  client_id: string | null;
  variant_name: string;
  variant_slug: string;
  variant_purpose: string | null;
  vapi_assistant_id: string | null;
  vapi_phone_number_id: string | null;
  is_default_inbound: boolean;
  status: string;
  current_prompt_hash: string | null;
  current_vapi_settings_snapshot: unknown;
  compiled_prompt_snapshot: string | null;
  /** Managed-lifecycle link (nullable for legacy variants such as Alice). */
  voice_agent_id: string | null;
  /** Managed provider account (nullable for legacy variants). */
  provider_account_id: string | null;
  created_at: string;
  updated_at: string;
}

interface VoiceAgentRow {
  id: string;
  client_id: string;
  lifecycle_status: string;
  published_version_id: string | null;
  current_draft_version_id: string | null;
}

interface VoiceDeploymentRow {
  id: string;
  voice_agent_id: string;
  operation: string;
  status: string;
  error_code: string | null;
  error_message: string | null;
  started_at: string;
  completed_at: string | null;
}

interface VoicePhoneAssignmentRow {
  id: string;
  voice_agent_id: string;
  status: string;
  assigned_at: string;
}

interface VoiceProviderAccountRow {
  id: string;
  status: string;
  webhook_key: string | null;
  provider_webhook_credential_id: string | null;
}

interface PromptDeploymentRow {
  id: string;
  agent_variant_id: string | null;
  compiled_prompt: string | null;
  prompt_hash: string | null;
  generated_by: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  deployed_to_vapi: boolean;
  deployed_at: string | null;
  deployment_result_json: unknown;
  deployment_error: string | null;
  business_context_snapshot_json: unknown;
  created_at: string;
}

interface RawEventRow {
  id: string;
  received_at: string;
  event_type: string | null;
  processed_status: string;
  processing_error: string | null;
  payload_json?: unknown;
}

interface ToolCallRow {
  id: string;
  vapi_call_id: string | null;
  tool_call_id: string | null;
  tool_name: string | null;
  parameters_json: unknown;
  result_json: unknown;
  status: string;
  error: string | null;
  created_at: string;
}

interface CallActionRow {
  id: string;
  action_type: string;
  target_record_type: string | null;
  target_record_id: string | null;
  payload_json: unknown;
  result_json: unknown;
  status: string;
  created_by: string | null;
  created_at: string;
}

interface OutboundRequestRow {
  id: string;
  vapi_call_id: string | null;
  target_phone: string;
  target_name: string | null;
  call_purpose: string | null;
  status: string;
  error: string | null;
  requested_by: string | null;
  requested_at: string;
  started_at: string | null;
  completed_at: string | null;
}

interface VoiceTenantScope {
  allClients: boolean;
  clientIds: string[];
  /**
   * Tenants to subtract from whatever the scope above admits.
   *
   * Exclusion exists so a portal can answer "everyone EXCEPT us" — the One Up
   * Admin Portal separates its own operational Calls (LeadLoop) from the
   * client-owned Calls it administers (Clients), and the second half cannot be
   * expressed as a list because the client roster changes.
   *
   * It can only ever narrow. It is applied AFTER the inclusion scope, never
   * instead of it, so no combination of request parameters can turn an
   * exclusion into a way of seeing a tenant the actor could not already see.
   */
  excludeClientIds: string[];
}

interface VoiceCursorPayload {
  startedAt: string | null;
  id: string;
}

const CALL_SUMMARY_SELECT = [
  'id', 'vapi_call_id', 'airtable_call_record_id', 'airtable_agent_record_id',
  'agent_variant_id', 'client_id', 'direction', 'phone_number', 'customer_number',
  'caller_name', 'started_at', 'ended_at', 'duration_seconds', 'duration_minutes',
  'total_cost', 'cost_breakdown_json', 'status', 'ended_reason', 'summary',
  'transcript_available', 'recording_url', 'stereo_recording_url', 'structured_data_json', 'call_disposition',
  'call_category', 'caller_relationship', 'lead_eligibility', 'triage_reasoning',
  'lead_quality', 'source_rating', 'grade_explanation', 'follow_up_required', 'next_action',
  'sentiment', 'lead_score', 'assistant_score',
  'contact_id', 'raw_payload_id',
  'linked_task_id', 'linked_lead_id', 'linked_contact_id', 'linked_organization_id',
  'action_metadata_json', 'has_issue', 'created_at', 'updated_at',
  'archived_at', 'archived_by',
].join(',');

const CALL_DETAIL_SELECT = `${CALL_SUMMARY_SELECT},transcript,analysis_json,structured_outputs_json`;
const VARIANT_SELECT = [
  'id', 'airtable_agent_record_id', 'client_id', 'variant_name', 'variant_slug',
  'variant_purpose', 'vapi_assistant_id', 'vapi_phone_number_id', 'is_default_inbound',
  'status', 'current_prompt_hash', 'current_vapi_settings_snapshot',
  'compiled_prompt_snapshot', 'voice_agent_id', 'provider_account_id',
  'created_at', 'updated_at',
].join(',');
const SAFE_IDENTIFIER = /^[A-Za-z0-9][A-Za-z0-9_-]{0,127}$/;
const AIRTABLE_RECORD_ID = /^rec[A-Za-z0-9]{14}$/;
const SAFE_FILTER = /^[A-Za-z0-9][A-Za-z0-9 _.-]{0,63}$/;
const SAFE_SEARCH = /^[A-Za-z0-9@.+_'\-\s]{1,100}$/;
const ISSUE_PATTERN = /(error|fail|unresolved)/i;
const CLIENT_FORBIDDEN_KEY = /(airtable|vapi|provider|cost|error|raw|internal|debug|secret|token|credential)/i;

export function resolveVoiceTenantScope(
  actor: AuthActor,
  requestedClientIds: string[] = [],
  forcedClientId?: string,
  excludeClientIds: string[] = [],
): VoiceTenantScope {
  const requested = uniqueIdentifiers(requestedClientIds, 'client_id');
  const actorClients = uniqueIdentifiers(actor.clientIds, 'actor client_id');
  /*
    Validated with the same identifier rules as every other tenant id, because
    it reaches the same PostgREST filter string. Exclusion is applied to the
    scope the rules below produce; it never participates in deciding it, so it
    cannot widen anything.
  */
  const excluded = uniqueIdentifiers(excludeClientIds, 'excluded client_id');

  if (forcedClientId) {
    assertIdentifier(forcedClientId, 'forced client_id');
    if (!isAdminActor(actor) && !actorClients.includes(forcedClientId)) {
      return { allClients: false, clientIds: [], excludeClientIds: excluded };
    }
    return { allClients: false, clientIds: [forcedClientId], excludeClientIds: excluded };
  }

  if (isAdminActor(actor)) {
    return requested.length > 0
      ? { allClients: false, clientIds: requested, excludeClientIds: excluded }
      : { allClients: true, clientIds: [], excludeClientIds: excluded };
  }

  const allowed = requested.length > 0
    ? actorClients.filter((clientId) => requested.includes(clientId))
    : actorClients;
  return { allClients: false, clientIds: allowed, excludeClientIds: excluded };
}

export async function listVoiceCalls(
  store: VoiceDataStore,
  actor: AuthActor,
  filters: VoiceCallFilters = {},
  options: VoiceServiceOptions = {},
): Promise<VoiceCallsResponse> {
  const scope = resolveVoiceTenantScope(actor, filters.clientIds, options.forcedClientId, filters.excludeClientIds);
  const limit = validateLimit(filters.limit);
  // Ingestion health is operational data. Gate it before the empty-scope
  // shortcut so a client never learns the filter exists by getting an empty page.
  if (filters.processingStatus?.length && !shouldIncludeAdmin(actor, options)) {
    throw new VoiceServiceError(
      'forbidden',
      'processing_status is available to platform administrators only.',
      403,
    );
  }
  if (scopeIsEmpty(scope)) {
    return { items: [], nextCursor: null, limit };
  }

  const dates = validateDateRange(filters.dateFrom, filters.dateTo);
  const query: Record<string, string | undefined> = {
    select: CALL_SUMMARY_SELECT,
    order: 'started_at.desc.nullslast,id.desc',
    limit: String(limit + 1),
  };
  applyTenantScope(query, scope);
  applyCommonCallFilters(query, filters, dates);
  applyCursor(query, decodeVoiceCursor(filters.cursor));

  const rows = await store.select<VapiCallRow>('vapi_calls', query);
  const pageRows = rows.slice(0, limit);
  const variants = await loadVariantsForCalls(store, pageRows, scope);
  const includeAdmin = shouldIncludeAdmin(actor, options);
  const items = pageRows.map((row) => serializeCallSummary(
    row,
    variants.get(row.agent_variant_id ?? ''),
    includeAdmin,
    options.lowValuePurposes,
    options.triageLabels,
  ));
  const last = pageRows.at(-1);
  return {
    items,
    nextCursor: rows.length > limit && last
      ? encodeVoiceCursor({ startedAt: last.started_at, id: last.id })
      : null,
    limit,
  };
}

export async function getVoiceCall(
  store: VoiceDataStore,
  actor: AuthActor,
  callId: string,
  options: VoiceServiceOptions = {},
): Promise<VoiceCallDetail> {
  const scope = resolveVoiceTenantScope(actor, [], options.forcedClientId);
  const row = await findCall(store, callId, scope, CALL_DETAIL_SELECT);
  if (!row) throw notFound('Voice call');
  const includeAdmin = shouldIncludeAdmin(actor, options);
  const variant = row.agent_variant_id
    ? (await store.select<VoiceAgentVariantRow>('voice_agent_variants', {
        select: VARIANT_SELECT,
        id: `eq.${row.agent_variant_id}`,
        limit: '1',
      }))[0]
    : undefined;
  const providerCallId = row.vapi_call_id ?? undefined;
  const [toolRows, actionRows, eventRows, noteRows, triageRows] = await Promise.all([
    providerCallId
      ? store.select<ToolCallRow>('vapi_tool_calls', {
          select: 'id,vapi_call_id,tool_call_id,tool_name,parameters_json,result_json,status,error,created_at',
          vapi_call_id: `eq.${providerCallId}`,
          order: 'created_at.desc',
          limit: '100',
        })
      : Promise.resolve([]),
    store.select<CallActionRow>('vapi_call_actions', {
      select: 'id,action_type,target_record_type,target_record_id,payload_json,result_json,status,created_by,created_at',
      or: providerCallId && row.airtable_call_record_id
        ? `(vapi_call_id.eq.${providerCallId},airtable_call_record_id.eq.${row.airtable_call_record_id},supabase_call_id.eq.${row.id})`
        : providerCallId
          ? `(vapi_call_id.eq.${providerCallId},supabase_call_id.eq.${row.id})`
          : `(supabase_call_id.eq.${row.id})`,
      order: 'created_at.desc',
      limit: '100',
    }),
    includeAdmin && row.raw_payload_id
      ? store.select<RawEventRow>('vapi_events_raw', {
          select: 'id,received_at,event_type,processed_status,processing_error,payload_json',
          or: providerCallId
            ? `(id.eq.${row.raw_payload_id},vapi_call_id.eq.${providerCallId})`
            : `(id.eq.${row.raw_payload_id})`,
          order: 'received_at.desc',
          limit: '100',
        })
      : Promise.resolve([]),
    store.select<RecordNoteRow>('record_notes', {
      select: 'id,body,author_id,author_name,created_at',
      client_id: `eq.${row.client_id}`,
      record_type: 'eq.call',
      record_id: `eq.${row.id}`,
      order: 'created_at.desc',
      limit: '200',
    }),
    store.select<CallTriageChangeRow>('call_triage_changes', {
      select: 'id,previous_caller_relationship,new_caller_relationship,previous_call_purpose,new_call_purpose,previous_lead_eligibility,new_lead_eligibility,changed_by_id,changed_by_name,changed_at',
      client_id: `eq.${row.client_id}`,
      call_id: `eq.${row.id}`,
      order: 'changed_at.desc',
      limit: '200',
    }),
  ]);

  const base = serializeCallSummary(row, variant, includeAdmin, options.lowValuePurposes, options.triageLabels);
  const recordingPathBase = options.recordingPathBase ?? '/api/voice/calls';
  const actions = actionRows.map((action) => ({
    type: action.action_type,
    status: action.status,
    targetType: optional(action.target_record_type),
    createdAt: action.created_at,
  }));
  const toolResults = toolRows.map((tool) => ({
    name: optional(tool.tool_name),
    status: tool.status,
    createdAt: tool.created_at,
  }));
  const latestEvent = eventRows[0];

  return {
    ...base,
    transcript: optional(row.transcript),
    structuredIntake: sanitizeClientValue(row.structured_data_json),
    structuredOutputs: parseStructuredOutputs(row.structured_outputs_json),
    gradeExplanation: optional(row.grade_explanation),
    recommendedNextAction: optional(row.next_action),
    contactId: optional(row.contact_id),
    notes: noteRows.map((note) => ({
      id: note.id,
      body: note.body,
      authorId: optional(note.author_id),
      authorName: note.author_name,
      createdAt: note.created_at,
    })),
    triageHistory: triageRows.map((change) => ({
      id: change.id,
      previousCallerRelationship: optional(change.previous_caller_relationship),
      newCallerRelationship: change.new_caller_relationship,
      previousCallPurpose: optional(change.previous_call_purpose),
      newCallPurpose: change.new_call_purpose,
      previousLeadEligibility: optional(change.previous_lead_eligibility),
      newLeadEligibility: change.new_lead_eligibility,
      changedById: optional(change.changed_by_id),
      changedByName: change.changed_by_name,
      changedAt: change.changed_at,
    })),
    actions,
    toolResults,
    recordingUrl: base.recordingAvailable
      ? `${recordingPathBase}/${encodeURIComponent(row.id)}/recording`
      : undefined,
    admin: includeAdmin
      ? {
          ...(base.admin ?? { provider: 'vapi' as const }),
          events: eventRows.map((event) => ({
            id: event.id,
            receivedAt: event.received_at,
            eventType: optional(event.event_type),
            status: event.processed_status,
            error: optional(event.processing_error),
            payload: event.payload_json,
          })),
          toolCalls: toolRows.map((tool) => ({
            id: tool.id,
            name: optional(tool.tool_name),
            providerToolCallId: optional(tool.tool_call_id),
            parameters: tool.parameters_json,
            result: tool.result_json,
            status: tool.status,
            error: optional(tool.error),
            createdAt: tool.created_at,
          })),
          actionHistory: actionRows.map((action) => ({
            id: action.id,
            type: action.action_type,
            status: action.status,
            targetType: optional(action.target_record_type),
            targetId: optional(action.target_record_id),
            payload: action.payload_json,
            result: action.result_json,
            createdBy: optional(action.created_by),
            createdAt: action.created_at,
          })),
          ingestionStatus: latestEvent?.processed_status,
          ingestionError: optional(latestEvent?.processing_error),
        }
      : undefined,
  };
}

export async function getVoiceRecordingReference(
  store: VoiceDataStore,
  actor: AuthActor,
  callId: string,
  options: VoiceServiceOptions = {},
): Promise<VoiceRecordingReference> {
  const scope = resolveVoiceTenantScope(actor, [], options.forcedClientId);
  const row = await findCall(
    store,
    callId,
    scope,
    'id,vapi_call_id,recording_url,stereo_recording_url,client_id',
  );
  if (!row) throw notFound('Voice call');
  const recordingUrl = optional(row.recording_url);
  const stereoRecordingUrl = optional(row.stereo_recording_url);
  return {
    callId: row.id,
    providerCallId:
      optional(row.vapi_call_id)
      ?? providerCallIdFromRecordingUrl(stereoRecordingUrl)
      ?? providerCallIdFromRecordingUrl(recordingUrl),
    recordingUrl,
    stereoRecordingUrl,
    available: Boolean(row.vapi_call_id || recordingUrl || stereoRecordingUrl),
  };
}

/**
 * Legacy Vapi storage keys were `{org UUID}-{timestamp}-{call UUID}-stereo.wav`.
 * Historical imports retained that URL but not the provider call column, so
 * the final UUID is the only safe way to use Vapi's authenticated artifact API.
 */
export function providerCallIdFromRecordingUrl(value?: string): string | undefined {
  if (!value) return undefined;
  try {
    const url = new URL(value);
    if (url.protocol !== 'https:') return undefined;
    const matches = url.pathname.match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi);
    return matches?.at(-1)?.toLowerCase();
  } catch {
    return undefined;
  }
}

export async function listVoiceAgents(
  store: VoiceDataStore,
  actor: AuthActor,
  filters: VoiceAgentFilters = {},
  options: VoiceServiceOptions = {},
): Promise<VoiceAgentsResponse> {
  const scope = resolveVoiceTenantScope(actor, filters.clientIds, options.forcedClientId, filters.excludeClientIds);
  if (scopeIsEmpty(scope)) {
    return { items: [], total: 0, configuration: configurationState(options) };
  }
  const limit = validateLimit(filters.limit ?? VOICE_MAX_PAGE_SIZE);
  const query: Record<string, string | undefined> = {
    select: VARIANT_SELECT,
    order: 'variant_name.asc,id.asc',
    limit: String(limit),
  };
  applyTenantScope(query, scope);
  if (filters.status) query.status = `eq.${validateFilter(filters.status, 'status')}`;
  let variants = await store.select<VoiceAgentVariantRow>('voice_agent_variants', query);
  if (filters.search?.trim()) {
    const needle = validateSearch(filters.search).toLowerCase();
    variants = variants.filter((row) =>
      [row.variant_name, row.variant_slug, row.variant_purpose]
        .filter((value): value is string => Boolean(value))
        .some((value) => value.toLowerCase().includes(needle)),
    );
  }

  const [metrics, setupStatuses, avatarUrls] = await Promise.all([
    loadAgentMetrics(store, variants, scope),
    assembleVoiceAgentSetupStatuses(store, variants),
    loadAgentAvatarUrls(store, variants),
  ]);
  const includeAdmin = shouldIncludeAdmin(actor, options);
  const items = variants.map((variant) =>
    serializeAgent(
      variant,
      metrics.get(variant.id),
      includeAdmin,
      setupStatuses.get(variant.id) ?? fallbackSetup(variant),
      variant.voice_agent_id ? avatarUrls.get(variant.voice_agent_id) : undefined,
    ),
  );
  return {
    items,
    total: items.length,
    configuration: items.length === 0 ? configurationState(options) : undefined,
  };
}

export async function getVoiceAgent(
  store: VoiceDataStore,
  actor: AuthActor,
  agentId: string,
  options: VoiceServiceOptions = {},
): Promise<VoiceAgentDetail> {
  validateRef(agentId, 'Voice agent id');
  const scope = resolveVoiceTenantScope(actor, [], options.forcedClientId);
  if (scopeIsEmpty(scope)) throw notFound('Voice agent');
  let variant: VoiceAgentVariantRow | undefined;
  if (!isAirtableRecordId(agentId)) {
    const query: Record<string, string | undefined> = {
      select: VARIANT_SELECT,
      id: `eq.${agentId}`,
      limit: '1',
    };
    applyTenantScope(query, scope);
    variant = (await store.select<VoiceAgentVariantRow>('voice_agent_variants', query))[0];
  }
  if (!variant) {
    const legacyQuery: Record<string, string | undefined> = {
      select: VARIANT_SELECT,
      airtable_agent_record_id: `eq.${agentId}`,
      limit: '1',
    };
    applyTenantScope(legacyQuery, scope);
    variant = (await store.select<VoiceAgentVariantRow>('voice_agent_variants', legacyQuery))[0];
  }
  if (!variant) throw notFound('Voice agent');

  const includeAdmin = shouldIncludeAdmin(actor, options);
  const calls = await listVoiceCalls(store, actor, { agentId: variant.id, limit: 20 }, options);
  const [deployments, events, tools, outbound] = includeAdmin
    ? await Promise.all([
        store.select<PromptDeploymentRow>('voice_agent_prompt_deployments', {
          select: 'id,agent_variant_id,compiled_prompt,prompt_hash,generated_by,reviewed_by,reviewed_at,deployed_to_vapi,deployed_at,deployment_result_json,deployment_error,business_context_snapshot_json,created_at',
          agent_variant_id: `eq.${variant.id}`,
          order: 'created_at.desc',
          limit: '1',
        }),
        variant.vapi_assistant_id
          ? store.select<RawEventRow>('vapi_events_raw', {
              select: 'id,received_at,event_type,processed_status,processing_error',
              vapi_assistant_id: `eq.${variant.vapi_assistant_id}`,
              order: 'received_at.desc',
              limit: '25',
            })
          : Promise.resolve([]),
        store.select<ToolCallRow>('vapi_tool_calls', {
          select: 'id,vapi_call_id,tool_call_id,tool_name,parameters_json,result_json,status,error,created_at',
          order: 'created_at.desc',
          limit: '25',
        }),
        store.select<OutboundRequestRow>('outbound_call_requests', {
          select: 'id,vapi_call_id,target_phone,target_name,call_purpose,status,error,requested_by,requested_at,started_at,completed_at',
          agent_variant_id: `eq.${variant.id}`,
          order: 'requested_at.desc',
          limit: '20',
        }),
      ])
    : [[], [], [], []] as const;
  const currentPromptDeployment = deployments[0]
    ? serializePromptDeployment(deployments[0])
    : undefined;
  const setupStatuses = await assembleVoiceAgentSetupStatuses(store, [variant]);
  const avatarUrls = await loadAgentAvatarUrls(store, [variant]);
  const summary = serializeAgent(
    variant,
    metricsFromCalls(calls.items),
    includeAdmin,
    setupStatuses.get(variant.id) ?? fallbackSetup(variant),
    variant.voice_agent_id ? avatarUrls.get(variant.voice_agent_id) : undefined,
  );

  return {
    ...summary,
    recentCalls: calls.items,
    admin: includeAdmin
      ? {
          ...(summary.admin ?? { provider: 'vapi' as const }),
          currentPromptDeployment,
          recentEvents: events.map((event) => ({
            id: event.id,
            receivedAt: event.received_at,
            eventType: optional(event.event_type),
            status: event.processed_status,
            error: optional(event.processing_error),
          })),
          recentToolCalls: tools.map((tool) => ({
            id: tool.id,
            name: optional(tool.tool_name),
            providerToolCallId: optional(tool.tool_call_id),
            parameters: tool.parameters_json,
            result: tool.result_json,
            status: tool.status,
            error: optional(tool.error),
            createdAt: tool.created_at,
          })),
          recentOutboundRequests: outbound.map((request) => ({
            id: request.id,
            providerCallId: optional(request.vapi_call_id),
            targetPhone: request.target_phone,
            targetName: optional(request.target_name),
            purpose: optional(request.call_purpose),
            status: request.status,
            error: optional(request.error),
            requestedBy: optional(request.requested_by),
            requestedAt: request.requested_at,
            startedAt: optional(request.started_at),
            completedAt: optional(request.completed_at),
          })),
        }
      : undefined,
  };
}

export async function getVoiceUsage(
  store: VoiceDataStore,
  actor: AuthActor,
  filters: VoiceUsageFilters = {},
  options: VoiceServiceOptions = {},
): Promise<VoiceUsageSummary> {
  const scope = resolveVoiceTenantScope(actor, filters.clientIds, options.forcedClientId, filters.excludeClientIds);
  const dates = validateDateRange(filters.dateFrom, filters.dateTo);
  const empty = emptyUsage(scope, dates);
  if (scopeIsEmpty(scope)) return empty;
  const query: Record<string, string | undefined> = {
    select: 'client_id,direction,duration_seconds,total_cost,follow_up_required,status,ended_reason,has_issue,started_at',
    order: 'started_at.desc.nullslast,id.desc',
    limit: '20001',
  };
  applyTenantScope(query, scope);
  applyCommonCallFilters(query, filters, dates);
  const rows = await store.select<VapiCallRow>('vapi_calls', query);
  const truncated = rows.length > 20000;
  const included = rows.slice(0, 20000);
  const includeCost = shouldIncludeAdmin(actor, options);
  const buckets = new Map<string, VoiceUsageBucket>();
  for (const row of included) {
    const clientId = row.client_id ?? 'unassigned';
    const bucket = buckets.get(clientId) ?? {
      clientId,
      callCount: 0,
      totalDurationSeconds: 0,
      totalMinutes: 0,
      inboundCallCount: 0,
      outboundCallCount: 0,
      followUpRequiredCount: 0,
      issueCount: 0,
      ...(includeCost ? { totalCost: 0 } : {}),
    };
    bucket.callCount += 1;
    bucket.totalDurationSeconds += numeric(row.duration_seconds) ?? 0;
    if (normalizeDirection(row.direction) === 'inbound') bucket.inboundCallCount += 1;
    if (normalizeDirection(row.direction) === 'outbound') bucket.outboundCallCount += 1;
    if (row.follow_up_required) bucket.followUpRequiredCount += 1;
    if (rowHasIssue(row)) bucket.issueCount += 1;
    if (includeCost) bucket.totalCost = round((bucket.totalCost ?? 0) + (numeric(row.total_cost) ?? 0), 6);
    buckets.set(clientId, bucket);
  }
  for (const bucket of buckets.values()) {
    bucket.totalMinutes = round(bucket.totalDurationSeconds / 60, 4);
  }
  const byClient = [...buckets.values()].sort((a, b) => a.clientId.localeCompare(b.clientId));
  const total = byClient.reduce((sum, bucket) => ({
    callCount: sum.callCount + bucket.callCount,
    totalDurationSeconds: sum.totalDurationSeconds + bucket.totalDurationSeconds,
    inboundCallCount: sum.inboundCallCount + bucket.inboundCallCount,
    outboundCallCount: sum.outboundCallCount + bucket.outboundCallCount,
    followUpRequiredCount: sum.followUpRequiredCount + bucket.followUpRequiredCount,
    issueCount: sum.issueCount + bucket.issueCount,
    totalCost: sum.totalCost + (bucket.totalCost ?? 0),
  }), {
    callCount: 0,
    totalDurationSeconds: 0,
    inboundCallCount: 0,
    outboundCallCount: 0,
    followUpRequiredCount: 0,
    issueCount: 0,
    totalCost: 0,
  });
  return {
    clientIds: scope.allClients ? null : scope.clientIds,
    dateFrom: dates.dateFrom ?? null,
    dateTo: dates.dateTo ?? null,
    callCount: total.callCount,
    totalDurationSeconds: total.totalDurationSeconds,
    totalMinutes: round(total.totalDurationSeconds / 60, 4),
    averageDurationSeconds: total.callCount ? round(total.totalDurationSeconds / total.callCount, 2) : 0,
    inboundCallCount: total.inboundCallCount,
    outboundCallCount: total.outboundCallCount,
    followUpRequiredCount: total.followUpRequiredCount,
    issueCount: total.issueCount,
    totalCost: includeCost ? round(total.totalCost, 6) : undefined,
    byClient,
    truncated,
  };
}

export function encodeVoiceCursor(payload: VoiceCursorPayload): string {
  validateRef(payload.id, 'Cursor id');
  if (payload.startedAt !== null) validateIsoDate(payload.startedAt, 'Cursor date');
  return base64UrlEncode(JSON.stringify(payload));
}

export function decodeVoiceCursor(cursor?: string): VoiceCursorPayload | undefined {
  if (!cursor) return undefined;
  try {
    const parsed = JSON.parse(base64UrlDecode(cursor)) as Partial<VoiceCursorPayload>;
    if (typeof parsed.id !== 'string' || (parsed.startedAt !== null && typeof parsed.startedAt !== 'string')) {
      throw new Error('shape');
    }
    validateRef(parsed.id, 'Cursor id');
    if (parsed.startedAt !== null) validateIsoDate(parsed.startedAt, 'Cursor date');
    return { id: parsed.id, startedAt: parsed.startedAt };
  } catch (error) {
    if (error instanceof VoiceServiceError) throw error;
    throw validation('Invalid cursor.');
  }
}

function serializeCallSummary(
  row: VapiCallRow,
  variant: VoiceAgentVariantRow | undefined,
  includeAdmin: boolean,
  lowValuePurposes: string[] = [],
  triageLabels: VoiceServiceOptions['triageLabels'] = {},
): VoiceCallSummary {
  const structured = asRecord(row.structured_data_json);
  const durationSeconds = numeric(row.duration_seconds);
  const durationMinutes = numeric(row.duration_minutes)
    ?? (durationSeconds !== undefined ? round(durationSeconds / 60, 4) : undefined);
  const callCategory = normalizeCallCategory(row.call_category);
  return {
    id: row.id,
    clientId: row.client_id ?? 'unassigned',
    agentId: optional(row.agent_variant_id),
    agentName: variant?.variant_name,
    caller: {
      name: optional(row.caller_name) ?? structuredString(structured, 'caller_full_name', 'full_name', 'name'),
      firstName: structuredString(structured, 'first_name', 'firstName'),
      lastName: structuredString(structured, 'last_name', 'lastName'),
      phone: optional(row.customer_number) ?? optional(row.phone_number) ?? structuredString(structured, 'phone_number', 'phone'),
      email: structuredString(structured, 'email_address', 'email'),
      businessName: structuredString(structured, 'business_name', 'company_name', 'business'),
    },
    direction: normalizeDirection(row.direction),
    status: optional(row.status),
    outcome: optional(row.call_disposition) ?? optional(row.ended_reason),
    startedAt: optional(row.started_at),
    endedAt: optional(row.ended_at),
    durationSeconds,
    durationMinutes,
    summary: optional(row.summary),
    callCategory,
    callCategoryLabel: triageLabels.purposes?.[callCategory],
    callerRelationship: optional(row.caller_relationship),
    callerRelationshipLabel: row.caller_relationship
      ? triageLabels.callerRelationships?.[row.caller_relationship]
      : undefined,
    leadEligibility: optional(row.lead_eligibility),
    leadEligibilityLabel: row.lead_eligibility
      ? triageLabels.leadEligibility?.[row.lead_eligibility]
      : undefined,
    triageReasoning: optional(row.triage_reasoning),
    isLowValue:
      lowValuePurposes.includes(callCategory)
      || sourceRating(row.source_rating) === 1
      || row.call_disposition === 'no_action_needed',
    archived: Boolean(row.archived_at),
    archivedAt: optional(row.archived_at),
    leadGrade: optional(row.lead_quality),
    sourceRating: sourceRating(row.source_rating),
    sentiment: optional(row.sentiment),
    leadScore: numeric(row.lead_score),
    assistantScore: numeric(row.assistant_score),
    followUpRequired: Boolean(row.follow_up_required),
    transcriptAvailable: row.transcript_available ?? Boolean(row.transcript),
    recordingAvailable: Boolean(row.vapi_call_id || row.recording_url || row.stereo_recording_url),
    hasIssue: rowHasIssue(row),
    admin: includeAdmin
      ? {
          provider: 'vapi',
          providerCallId: optional(row.vapi_call_id),
          providerAssistantId: optional(variant?.vapi_assistant_id),
          providerPhoneNumberId: optional(variant?.vapi_phone_number_id),
          totalCost: numeric(row.total_cost),
          costBreakdown: row.cost_breakdown_json,
          airtableCallRecordId: optional(row.airtable_call_record_id),
          airtableAgentRecordId: optional(row.airtable_agent_record_id),
          rawPayloadId: optional(row.raw_payload_id),
          linkedTaskId: optional(row.linked_task_id),
          linkedLeadId: optional(row.linked_lead_id),
          linkedContactId: optional(row.linked_contact_id),
          linkedOrganizationId: optional(row.linked_organization_id),
          leadQuality: optional(row.lead_quality),
          nextAction: optional(row.next_action),
          debugMetadata: row.action_metadata_json,
        }
      : undefined,
  };
}

function serializeAgent(
  row: VoiceAgentVariantRow,
  metrics: VoiceAgentSummary['metrics'] | undefined,
  includeAdmin: boolean,
  setup: VoiceAgentSetupStatus,
  avatarUrl?: string,
): VoiceAgentSummary {
  return {
    id: row.id,
    voiceAgentId: optional(row.voice_agent_id),
    clientId: row.client_id ?? 'unassigned',
    displayName: row.variant_name,
    avatarUrl,
    slug: row.variant_slug,
    purpose: optional(row.variant_purpose),
    status: row.status,
    isDefaultInbound: row.is_default_inbound,
    mapped: Boolean(row.vapi_assistant_id && row.client_id && /mapped|active|synced/i.test(row.status)),
    phoneMapped: Boolean(row.vapi_phone_number_id),
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    setupStatus: redactSetupStatusForClient(setup),
    metrics: metrics ?? {
      recentCallCount: 0,
      followUpCount: 0,
      issueCount: 0,
      statusIndicator: /error|failed/i.test(row.status) ? 'error' : 'unknown',
    },
    admin: includeAdmin
      ? {
          provider: 'vapi',
          providerAssistantId: optional(row.vapi_assistant_id),
          providerPhoneNumberId: optional(row.vapi_phone_number_id),
          airtableAgentRecordId: optional(row.airtable_agent_record_id),
          currentPromptHash: optional(row.current_prompt_hash),
          currentSettings: row.current_vapi_settings_snapshot,
          compiledPrompt: optional(row.compiled_prompt_snapshot),
          setup,
        }
      : undefined,
  };
}

async function loadAgentAvatarUrls(
  store: VoiceDataStore,
  variants: VoiceAgentVariantRow[],
): Promise<Map<string, string>> {
  const agentIds = [...new Set(variants.map((variant) => optional(variant.voice_agent_id)).filter((id): id is string => Boolean(id)))];
  if (agentIds.length === 0) return new Map();
  const agents = await store.select<{
    id: string;
    current_draft_version_id: string | null;
    published_version_id: string | null;
  }>('voice_agents', {
    select: 'id,current_draft_version_id,published_version_id',
    id: `in.(${agentIds.join(',')})`,
  });
  const versionByAgent = new Map(agents.map((agent) => [
    agent.id,
    agent.current_draft_version_id ?? agent.published_version_id,
  ]));
  const versionIds = [...new Set([...versionByAgent.values()].filter((id): id is string => Boolean(id)))];
  if (versionIds.length === 0) return new Map();
  const versions = await store.select<{
    id: string;
    voice_agent_id: string;
    compiled_persona_snapshot: unknown;
  }>('voice_agent_versions', {
    select: 'id,voice_agent_id,compiled_persona_snapshot',
    id: `in.(${versionIds.join(',')})`,
  });
  const result = new Map<string, string>();
  for (const version of versions) {
    if (versionByAgent.get(version.voice_agent_id) !== version.id) continue;
    const profile = asRecord(asRecord(version.compiled_persona_snapshot)?.portalProfile);
    const avatarUrl = structuredString(profile, 'avatarUrl');
    if (avatarUrl) result.set(version.voice_agent_id, avatarUrl);
  }
  return result;
}

/**
 * Assemble the authoritative setup-status inputs for a set of variants from the
 * best available Supabase facts, then derive each canonical status. This is the
 * single server-side source of truth consumed by Admin and every client portal;
 * frontends never re-derive readiness from partial data.
 *
 * Legacy variants (no managed voice_agent_id, e.g. Alice) are supported: their
 * "activation" is a live mapped+active variant status and their phone is the
 * legacy variant field (compatibility only). Even so, `ready` still requires a
 * successfully processed call — never a mapped assistant or legacy phone alone.
 */
export async function assembleVoiceAgentSetupStatuses(
  store: VoiceDataStore,
  variants: VoiceAgentVariantRow[],
): Promise<Map<string, VoiceAgentSetupStatus>> {
  const result = new Map<string, VoiceAgentSetupStatus>();
  if (variants.length === 0) return result;

  const agentIds = uniqueIds(variants.map((v) => v.voice_agent_id));
  const assistantIds = uniqueIds(variants.map((v) => v.vapi_assistant_id));
  const accountIds = uniqueIds(variants.map((v) => v.provider_account_id));

  const [agents, deployments, assignments, accounts, processedEvents, latestEvents] = await Promise.all([
    agentIds.length
      ? store.select<VoiceAgentRow>('voice_agents', {
          select: 'id,client_id,lifecycle_status,published_version_id,current_draft_version_id',
          id: `in.(${agentIds.join(',')})`,
          limit: String(agentIds.length),
        })
      : Promise.resolve([]),
    agentIds.length
      ? store.select<VoiceDeploymentRow>('voice_agent_deployments', {
          select: 'id,voice_agent_id,operation,status,error_code,error_message,started_at,completed_at',
          voice_agent_id: `in.(${agentIds.join(',')})`,
          order: 'started_at.desc',
          limit: '500',
        })
      : Promise.resolve([]),
    agentIds.length
      ? store.select<VoicePhoneAssignmentRow>('voice_agent_phone_assignments', {
          select: 'id,voice_agent_id,status,assigned_at',
          voice_agent_id: `in.(${agentIds.join(',')})`,
          status: 'eq.active',
          limit: '500',
        })
      : Promise.resolve([]),
    accountIds.length
      ? store.select<VoiceProviderAccountRow>('voice_provider_accounts', {
          select: 'id,status,webhook_key,provider_webhook_credential_id',
          id: `in.(${accountIds.join(',')})`,
          limit: String(accountIds.length),
        })
      : Promise.resolve([]),
    assistantIds.length
      ? store.select<{ vapi_assistant_id: string | null }>('vapi_events_raw', {
          select: 'vapi_assistant_id',
          vapi_assistant_id: `in.(${assistantIds.join(',')})`,
          processed_status: 'in.(processed,processed_with_warnings)',
          limit: '1000',
        })
      : Promise.resolve([]),
    assistantIds.length
      ? store.select<{ vapi_assistant_id: string | null; processed_status: string; processing_error: string | null; received_at: string }>('vapi_events_raw', {
          select: 'vapi_assistant_id,processed_status,processing_error,received_at',
          vapi_assistant_id: `in.(${assistantIds.join(',')})`,
          order: 'received_at.desc',
          limit: '1000',
        })
      : Promise.resolve([]),
  ]);

  const agentById = new Map(agents.map((a) => [a.id, a]));
  const accountById = new Map(accounts.map((a) => [a.id, a]));
  const activeAssignmentByAgent = new Set(assignments.map((a) => a.voice_agent_id));
  const latestDeploymentByAgent = new Map<string, VoiceDeploymentRow>();
  const succeededActivationByAgent = new Set<string>();
  for (const dep of deployments) {
    if (!latestDeploymentByAgent.has(dep.voice_agent_id)) latestDeploymentByAgent.set(dep.voice_agent_id, dep);
    if (dep.status === 'succeeded' && (dep.operation === 'create' || dep.operation === 'update')) {
      succeededActivationByAgent.add(dep.voice_agent_id);
    }
  }
  const processedAssistants = new Set(processedEvents.map((e) => e.vapi_assistant_id).filter(Boolean));
  const latestEventByAssistant = new Map<string, { processed_status: string; processing_error: string | null; received_at: string }>();
  for (const e of latestEvents) {
    if (e.vapi_assistant_id && !latestEventByAssistant.has(e.vapi_assistant_id)) {
      latestEventByAssistant.set(e.vapi_assistant_id, e);
    }
  }

  for (const variant of variants) {
    const managed = Boolean(variant.voice_agent_id);
    const agent = variant.voice_agent_id ? agentById.get(variant.voice_agent_id) : undefined;
    const account = variant.provider_account_id ? accountById.get(variant.provider_account_id) : undefined;
    const assistantLinked = Boolean(variant.vapi_assistant_id && variant.client_id);
    const legacyActive = /mapped|active|synced/i.test(variant.status);
    const hasProcessedCall = Boolean(variant.vapi_assistant_id && processedAssistants.has(variant.vapi_assistant_id));

    const archived = managed
      ? agent?.lifecycle_status === 'archived'
      : /archiv|retired|disabled/i.test(variant.status);

    const activationVerified = managed
      ? (variant.voice_agent_id ? succeededActivationByAgent.has(variant.voice_agent_id) : false)
      : assistantLinked && legacyActive;

    const phoneAssignmentCanonical = managed && Boolean(variant.voice_agent_id) && activeAssignmentByAgent.has(variant.voice_agent_id!);
    const phoneAssigned = phoneAssignmentCanonical || Boolean(variant.vapi_phone_number_id);

    const webhookSecured = managed
      ? Boolean(account && account.status === 'active' && account.webhook_key)
      : true;

    const materialFailure = resolveMaterialFailure(
      managed ? (variant.voice_agent_id ? latestDeploymentByAgent.get(variant.voice_agent_id) : undefined) : undefined,
      variant.vapi_assistant_id ? latestEventByAssistant.get(variant.vapi_assistant_id) : undefined,
    );

    const inputs: VoiceAgentSetupInputs = {
      archived,
      assistantLinked,
      managed,
      hasPublishedVersion: managed ? Boolean(agent?.published_version_id) : false,
      activationVerified,
      phoneAssigned,
      phoneAssignmentCanonical,
      webhookSecured,
      hasProcessedCall,
      materialFailure,
    };
    result.set(variant.id, deriveVoiceAgentSetupStatus(inputs));
  }

  return result;
}

function resolveMaterialFailure(
  latestDeployment: VoiceDeploymentRow | undefined,
  latestEvent: { processed_status: string; processing_error: string | null; received_at: string } | undefined,
): VoiceAgentSetupFailure | null {
  if (latestDeployment && ['failed', 'indeterminate', 'conflicted'].includes(latestDeployment.status)) {
    return {
      source: latestDeployment.error_code === 'provider_verification_failed' ? 'provider_drift' : 'deployment',
      code: latestDeployment.error_code ?? latestDeployment.status,
      message: latestDeployment.error_message ?? undefined,
      at: latestDeployment.completed_at ?? latestDeployment.started_at,
    };
  }
  if (latestEvent && ['failed', 'unresolved'].includes(latestEvent.processed_status)) {
    return {
      source: 'ingestion',
      code: latestEvent.processed_status,
      message: latestEvent.processing_error ?? undefined,
      at: latestEvent.received_at,
    };
  }
  return null;
}

function uniqueIds(values: Array<string | null>): string[] {
  return [...new Set(values.filter((v): v is string => Boolean(v)))];
}

/** Defensive default when a status could not be assembled (should not occur). */
function fallbackSetup(variant: VoiceAgentVariantRow): VoiceAgentSetupStatus {
  return deriveVoiceAgentSetupStatus({
    archived: /archiv|retired|disabled/i.test(variant.status),
    assistantLinked: Boolean(variant.vapi_assistant_id && variant.client_id),
    managed: Boolean(variant.voice_agent_id),
    hasPublishedVersion: false,
    activationVerified: false,
    phoneAssigned: Boolean(variant.vapi_phone_number_id),
    phoneAssignmentCanonical: false,
    webhookSecured: false,
    hasProcessedCall: false,
    materialFailure: null,
  });
}

async function loadVariantsForCalls(
  store: VoiceDataStore,
  calls: VapiCallRow[],
  scope: VoiceTenantScope,
): Promise<Map<string, VoiceAgentVariantRow>> {
  const ids = [...new Set(calls.map((row) => row.agent_variant_id).filter((id): id is string => Boolean(id)))];
  if (ids.length === 0) return new Map();
  const query: Record<string, string | undefined> = {
    select: VARIANT_SELECT,
    id: `in.(${ids.join(',')})`,
    limit: String(ids.length),
  };
  applyTenantScope(query, scope);
  const variants = await store.select<VoiceAgentVariantRow>('voice_agent_variants', query);
  return new Map(variants.map((variant) => [variant.id, variant]));
}

async function loadAgentMetrics(
  store: VoiceDataStore,
  variants: VoiceAgentVariantRow[],
  scope: VoiceTenantScope,
): Promise<Map<string, VoiceAgentSummary['metrics']>> {
  if (variants.length === 0) return new Map();
  const ids = variants.map((variant) => variant.id);
  const query: Record<string, string | undefined> = {
    select: 'id,agent_variant_id,started_at,follow_up_required,status,ended_reason,has_issue,client_id',
    agent_variant_id: `in.(${ids.join(',')})`,
    order: 'started_at.desc.nullslast,id.desc',
    limit: '5000',
  };
  applyTenantScope(query, scope);
  const rows = await store.select<VapiCallRow>('vapi_calls', query);
  const grouped = new Map<string, VoiceCallSummary[]>();
  for (const row of rows) {
    if (!row.agent_variant_id) continue;
    const list = grouped.get(row.agent_variant_id) ?? [];
    list.push({
      id: row.id,
      clientId: row.client_id ?? 'unassigned',
      caller: {},
      startedAt: optional(row.started_at),
      followUpRequired: Boolean(row.follow_up_required),
      transcriptAvailable: false,
      recordingAvailable: false,
      hasIssue: rowHasIssue(row),
    });
    grouped.set(row.agent_variant_id, list);
  }
  return new Map(variants.map((variant) => [variant.id, metricsFromCalls(grouped.get(variant.id) ?? [])]));
}

function metricsFromCalls(calls: VoiceCallSummary[]): VoiceAgentSummary['metrics'] {
  const issueCount = calls.filter((call) => call.hasIssue).length;
  const followUpCount = calls.filter((call) => call.followUpRequired).length;
  return {
    recentCallCount: calls.length,
    lastCallAt: calls.map((call) => call.startedAt).filter((value): value is string => Boolean(value)).sort().at(-1),
    followUpCount,
    issueCount,
    statusIndicator: issueCount > 0 ? 'error' : followUpCount > 0 ? 'warning' : calls.length > 0 ? 'ok' : 'unknown',
  };
}

async function findCall(
  store: VoiceDataStore,
  callRef: string,
  scope: VoiceTenantScope,
  select: string,
): Promise<VapiCallRow | undefined> {
  validateRef(callRef, 'Voice call id');
  if (scopeIsEmpty(scope)) return undefined;
  if (!isAirtableRecordId(callRef)) {
    const query: Record<string, string | undefined> = { select, id: `eq.${callRef}`, limit: '1' };
    applyTenantScope(query, scope);
    const row = (await store.select<VapiCallRow>('vapi_calls', query))[0];
    if (row) return row;
  }
  const compatibilityQuery: Record<string, string | undefined> = {
    select,
    or: `(vapi_call_id.eq.${callRef},airtable_call_record_id.eq.${callRef})`,
    limit: '1',
  };
  applyTenantScope(compatibilityQuery, scope);
  return (await store.select<VapiCallRow>('vapi_calls', compatibilityQuery))[0];
}

function applyTenantScope(query: Record<string, string | undefined>, scope: VoiceTenantScope): void {
  if (!scope.allClients) query.client_id = `in.(${scope.clientIds.join(',')})`;
  /*
    Subtraction, applied after inclusion. PostgREST cannot take two filters on
    the same column in one query string, so an inclusion list is narrowed in
    memory and only the leftovers are sent; a pure exclusion (the common case —
    "every client except ourselves") becomes a NOT IN.

    An inclusion list that excludes everything must NOT fall through to an
    unfiltered query, so the empty result is expressed as an impossible filter
    rather than by omitting the clause.
  */
  if (scope.excludeClientIds.length === 0) return;
  if (scope.allClients) {
    query.client_id = `not.in.(${scope.excludeClientIds.join(',')})`;
    return;
  }
  query.client_id = `in.(${effectiveScopeIds(scope).join(',')})`;
}

/** The inclusion list with the exclusions removed. */
function effectiveScopeIds(scope: VoiceTenantScope): string[] {
  return scope.clientIds.filter((id) => !scope.excludeClientIds.includes(id));
}

/**
 * Does this scope admit nothing at all?
 *
 * Callers check this BEFORE building a query, because "no tenants" has no
 * expression as a PostgREST filter — an empty `in.()` is a Postgres syntax
 * error, and omitting the clause would return every row, which is the exact
 * opposite of what an empty scope means.
 */
function scopeIsEmpty(scope: VoiceTenantScope): boolean {
  if (scope.allClients) return false;
  return effectiveScopeIds(scope).length === 0;
}

function applyCommonCallFilters(
  query: Record<string, string | undefined>,
  filters: Pick<
    VoiceCallFilters,
    'agentId' | 'direction' | 'status' | 'search' | 'hasIssue'
    | 'category' | 'excludeCategory' | 'leadGrade' | 'sourceRating' | 'excludeSourceRating'
    | 'feed' | 'lowValueCategories' | 'followUp' | 'processingStatus' | 'archived'
  >,
  dates: { dateFrom?: string; dateTo?: string },
): void {
  const andConditions: string[] = [];
  if (filters.agentId) {
    validateRef(filters.agentId, 'agent_id');
    if (isAirtableRecordId(filters.agentId)) {
      query.airtable_agent_record_id = `eq.${filters.agentId}`;
    } else {
      query.agent_variant_id = `eq.${filters.agentId}`;
    }
  }
  if (filters.direction) query.direction = directionFilter(filters.direction);
  if (filters.status) query.status = `eq.${validateFilter(filters.status, 'status')}`;
  if (dates.dateFrom) query.started_at = `gte.${dates.dateFrom}`;
  if (dates.dateTo) {
    if (dates.dateFrom) {
      andConditions.push(`started_at.gte.${dates.dateFrom}`, `started_at.lte.${dates.dateTo}`);
      delete query.started_at;
    } else {
      query.started_at = `lte.${dates.dateTo}`;
    }
  }
  if (filters.search?.trim()) query.search_document = `wfts(simple).${validateSearch(filters.search)}`;
  if (filters.hasIssue !== undefined) query.has_issue = `eq.${filters.hasIssue}`;
  if (filters.category?.length) {
    const categories = validateEnumList(filters.category, CALL_CATEGORIES, 'category') as CallCategory[];
    const storageValues = categories.flatMap(callCategoryStorageValues);
    if (!categories.includes('other') && !filters.excludeCategory?.length) {
      query.call_category = `in.(${storageValues.join(',')})`;
    } else if (categories.includes('other')) {
      andConditions.push(
        `or(call_category.is.null,call_category.in.(${storageValues.join(',')}),call_category.not.in.(${knownCallCategoryStorageValues().join(',')}))`,
      );
    } else {
      andConditions.push(`call_category.in.(${storageValues.join(',')})`);
    }
  }
  if (filters.excludeCategory?.length) {
    const categories = validateEnumList(filters.excludeCategory, CALL_CATEGORIES, 'exclude_category') as CallCategory[];
    const storageValues = categories.flatMap(callCategoryStorageValues);
    // SQL NOT IN excludes NULL. The explicit NULL branch keeps unclassified
    // and uncertain legitimate calls in the Main Feed.
    andConditions.push(`or(call_category.is.null,call_category.not.in.(${storageValues.join(',')}))`);
  }
  if (filters.leadGrade?.length) {
    query.lead_quality = `in.(${validateEnumList(filters.leadGrade, LEAD_GRADES, 'lead_grade').join(',')})`;
  }
  if (filters.sourceRating?.length) {
    query.source_rating = `in.(${validateSourceRatings(filters.sourceRating, 'source_rating').join(',')})`;
  }
  if (filters.excludeSourceRating?.length) {
    const values = validateSourceRatings(filters.excludeSourceRating, 'exclude_source_rating');
    // SQL NOT IN excludes NULL. Unrated calls remain visible in the default feed.
    andConditions.push(`or(source_rating.is.null,source_rating.not.in.(${values.join(',')}))`);
  }
  if (filters.feed) {
    const categories = validateEnumList(
      filters.lowValueCategories ?? [],
      CALL_CATEGORIES,
      'low_value_categories',
    ) as CallCategory[];
    const categoryValues = categories.flatMap(callCategoryStorageValues);
    const lowValueTerms = [
      ...(categoryValues.length ? [`call_category.in.(${categoryValues.join(',')})`] : []),
      'source_rating.eq.1',
      'call_disposition.eq.no_action_needed',
    ];
    if (filters.feed === 'low_value') {
      andConditions.push(`or(${lowValueTerms.join(',')})`);
    } else {
      if (categoryValues.length) {
        andConditions.push(`or(call_category.is.null,call_category.not.in.(${categoryValues.join(',')}))`);
      }
      andConditions.push('or(source_rating.is.null,source_rating.neq.1)');
      andConditions.push('or(call_disposition.is.null,call_disposition.neq.no_action_needed)');
    }
  }
  if (filters.followUp !== undefined) query.follow_up_required = `eq.${filters.followUp}`;
  if (filters.archived !== undefined) query.archived_at = filters.archived ? 'not.is.null' : 'is.null';
  if (filters.processingStatus?.length) {
    const values = validateEnumList(filters.processingStatus, PROCESSING_STATUSES, 'processing_status');
    query.processing_status = `in.(${values.join(',')})`;
  }
  if (andConditions.length) query.and = `(${andConditions.join(',')})`;
}

const PROCESSING_STATUSES = [
  'received', 'processed', 'processed_with_warnings', 'unresolved', 'failed',
] as const;

/** Closed-vocabulary filter validation. An unknown value is a 400, never a silent no-op. */
function validateEnumList(input: string[], allowed: readonly string[], label: string): string[] {
  const values = [...new Set(input.map((value) => value.trim().toLowerCase()).filter(Boolean))];
  if (values.length === 0) return [];
  const invalid = values.filter((value) => !allowed.includes(value));
  if (invalid.length > 0) {
    throw validation(`${label} contains unsupported value(s): ${invalid.join(', ')}.`);
  }
  return values;
}

function validateSourceRatings(input: number[], label: string): number[] {
  const values = [...new Set(input)];
  const invalid = values.filter((value) => !Number.isInteger(value) || value < 1 || value > 5);
  if (invalid.length > 0) {
    throw validation(`${label} contains unsupported value(s): ${invalid.join(', ')}.`);
  }
  return values;
}

function sourceRating(value: number | string | null | undefined): number | undefined {
  const parsed = numeric(value);
  return parsed !== undefined && Number.isInteger(parsed) && parsed >= 1 && parsed <= 5
    ? parsed
    : undefined;
}

function applyCursor(query: Record<string, string | undefined>, cursor: VoiceCursorPayload | undefined): void {
  if (!cursor) return;
  if (cursor.startedAt === null) {
    query.started_at = 'is.null';
    query.id = `lt.${cursor.id}`;
    return;
  }
  query.or = `(started_at.lt.${cursor.startedAt},and(started_at.eq.${cursor.startedAt},id.lt.${cursor.id}),started_at.is.null)`;
}

function directionFilter(value: string): string {
  const normalized = value.trim().toLowerCase();
  if (normalized === 'inbound') return 'in.(inbound,inboundPhoneCall)';
  if (normalized === 'outbound') return 'in.(outbound,outboundPhoneCall)';
  throw validation('direction must be inbound or outbound.');
}

function validateDateRange(dateFrom?: string, dateTo?: string): { dateFrom?: string; dateTo?: string } {
  const normalizedFrom = normalizeDate(dateFrom, false, 'date_from');
  const normalizedTo = normalizeDate(dateTo, true, 'date_to');
  if (normalizedFrom && normalizedTo && Date.parse(normalizedFrom) > Date.parse(normalizedTo)) {
    throw validation('date_from must be earlier than or equal to date_to.');
  }
  return { dateFrom: normalizedFrom, dateTo: normalizedTo };
}

function normalizeDate(value: string | undefined, endOfDay: boolean, label: string): string | undefined {
  if (!value) return undefined;
  const trimmed = value.trim();
  const expanded = /^\d{4}-\d{2}-\d{2}$/.test(trimmed)
    ? `${trimmed}T${endOfDay ? '23:59:59.999' : '00:00:00.000'}Z`
    : trimmed;
  validateIsoDate(expanded, label);
  return new Date(expanded).toISOString();
}

function validateIsoDate(value: string, label: string): void {
  if (!Number.isFinite(Date.parse(value))) throw validation(`${label} must be a valid ISO-8601 date.`);
}

function validateLimit(value: number | undefined): number {
  const candidate = value ?? VOICE_DEFAULT_PAGE_SIZE;
  if (!Number.isInteger(candidate) || candidate < 1 || candidate > VOICE_MAX_PAGE_SIZE) {
    throw validation(`limit must be an integer from 1 to ${VOICE_MAX_PAGE_SIZE}.`);
  }
  return candidate;
}

function validateFilter(value: string, label: string): string {
  const trimmed = value.trim();
  if (!SAFE_FILTER.test(trimmed)) throw validation(`${label} contains unsupported characters.`);
  return trimmed;
}

function validateSearch(value: string): string {
  const trimmed = value.trim();
  if (!SAFE_SEARCH.test(trimmed)) {
    throw validation('search must be 1-100 characters and contain only letters, numbers, spaces, and common contact punctuation.');
  }
  return trimmed;
}

function validateRef(value: string, label: string): void {
  if (!SAFE_IDENTIFIER.test(value)) throw validation(`${label} is invalid.`);
}

function isAirtableRecordId(value: string): boolean {
  return AIRTABLE_RECORD_ID.test(value);
}

function assertIdentifier(value: string, label: string): void {
  if (!SAFE_IDENTIFIER.test(value)) throw validation(`${label} is invalid.`);
}

function uniqueIdentifiers(values: string[], label: string): string[] {
  const unique = [...new Set(values.map((value) => value.trim()).filter(Boolean))];
  for (const value of unique) assertIdentifier(value, label);
  return unique;
}

function shouldIncludeAdmin(actor: AuthActor, options: VoiceServiceOptions): boolean {
  return options.includeAdminFields ?? isAdminActor(actor);
}

function configurationState(options: VoiceServiceOptions): VoiceConfigurationState | undefined {
  if (!options.forcedClientId) return undefined;
  return {
    status: 'not_configured',
    message: options.notConfiguredMessage ?? VOICE_CASCADE_NOT_CONFIGURED_MESSAGE,
    requiredProductionData: [
      'Vapi assistant ID',
      'Vapi phone number ID',
      'Variant slug',
      'client_id',
      'Agent/persona relationship',
      'Active/mapped status',
    ],
  };
}

function emptyUsage(scope: VoiceTenantScope, dates: { dateFrom?: string; dateTo?: string }): VoiceUsageSummary {
  return {
    clientIds: scope.allClients ? null : scope.clientIds,
    dateFrom: dates.dateFrom ?? null,
    dateTo: dates.dateTo ?? null,
    callCount: 0,
    totalDurationSeconds: 0,
    totalMinutes: 0,
    averageDurationSeconds: 0,
    inboundCallCount: 0,
    outboundCallCount: 0,
    followUpRequiredCount: 0,
    issueCount: 0,
    byClient: [],
    truncated: false,
  };
}

function serializePromptDeployment(row: PromptDeploymentRow): VoicePromptDeploymentSummary {
  return {
    id: row.id,
    compiledPrompt: optional(row.compiled_prompt),
    promptHash: optional(row.prompt_hash),
    generatedBy: optional(row.generated_by),
    reviewedBy: optional(row.reviewed_by),
    reviewedAt: optional(row.reviewed_at),
    deployed: row.deployed_to_vapi,
    deployedAt: optional(row.deployed_at),
    result: row.deployment_result_json,
    error: optional(row.deployment_error),
    businessContext: row.business_context_snapshot_json,
    createdAt: row.created_at,
  };
}

function sanitizeClientValue(value: unknown, depth = 0): unknown {
  if (depth > 5) return undefined;
  if (Array.isArray(value)) {
    return value.slice(0, 100).map((item) => sanitizeClientValue(item, depth + 1)).filter((item) => item !== undefined);
  }
  const record = asRecord(value);
  if (record) {
    return Object.fromEntries(
      Object.entries(record)
        .filter(([key]) => !CLIENT_FORBIDDEN_KEY.test(key))
        .slice(0, 100)
        .map(([key, item]) => [key, sanitizeClientValue(item, depth + 1)])
        .filter((entry) => entry[1] !== undefined),
    );
  }
  if (typeof value === 'string') return value.slice(0, 20_000);
  if (typeof value === 'number' || typeof value === 'boolean' || value === null) return value;
  return undefined;
}

function rowHasIssue(row: Pick<VapiCallRow, 'has_issue' | 'status' | 'ended_reason'>): boolean {
  return row.has_issue ?? ISSUE_PATTERN.test(`${row.status ?? ''} ${row.ended_reason ?? ''}`);
}

function normalizeDirection(value: string | null): string | undefined {
  if (!value) return undefined;
  const normalized = value.toLowerCase();
  if (normalized.includes('inbound')) return 'inbound';
  if (normalized.includes('outbound')) return 'outbound';
  return value;
}

function structuredString(record: Record<string, unknown> | undefined, ...keys: string[]): string | undefined {
  if (!record) return undefined;
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return undefined;
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : undefined;
}

function numeric(value: number | string | null | undefined): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
}

function optional<T>(value: T | null | undefined): T | undefined {
  return value ?? undefined;
}

function round(value: number, decimals: number): number {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

function validation(message: string): VoiceServiceError {
  return new VoiceServiceError('validation_error', message, 400);
}

function notFound(resource: string): VoiceServiceError {
  return new VoiceServiceError('not_found', `${resource} not found.`, 404);
}

function base64UrlEncode(value: string): string {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
  let output = '';
  for (let index = 0; index < value.length; index += 3) {
    const first = value.charCodeAt(index);
    const second = index + 1 < value.length ? value.charCodeAt(index + 1) : 0;
    const third = index + 2 < value.length ? value.charCodeAt(index + 2) : 0;
    const chunk = (first << 16) | (second << 8) | third;
    output += alphabet[(chunk >> 18) & 63];
    output += alphabet[(chunk >> 12) & 63];
    output += index + 1 < value.length ? alphabet[(chunk >> 6) & 63] : '=';
    output += index + 2 < value.length ? alphabet[chunk & 63] : '=';
  }
  return output.replace(/=/g, '').replace(/\+/g, '-').replace(/\//g, '_');
}

function base64UrlDecode(value: string): string {
  if (!/^[A-Za-z0-9_-]+$/.test(value)) throw new Error('invalid base64url');
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
  let output = '';
  for (let index = 0; index < padded.length; index += 4) {
    const values = [...padded.slice(index, index + 4)].map((char) => char === '=' ? 0 : alphabet.indexOf(char));
    if (values.some((item) => item < 0)) throw new Error('invalid base64url');
    const first = values[0] ?? 0;
    const second = values[1] ?? 0;
    const third = values[2] ?? 0;
    const fourth = values[3] ?? 0;
    const chunk = (first << 18) | (second << 12) | (third << 6) | fourth;
    output += String.fromCharCode((chunk >> 16) & 255);
    if (padded[index + 2] !== '=') output += String.fromCharCode((chunk >> 8) & 255);
    if (padded[index + 3] !== '=') output += String.fromCharCode(chunk & 255);
  }
  return output;
}
